import type { TankSnapshot } from './protocol.js';

/** sample() 的插值结果：远程坦克一帧的渲染状态。 */
export interface RemoteSample {
  x: number;
  z: number;
  /** 车体朝向（弧度，最短弧插值）。 */
  body: number;
  /** 炮塔朝向（弧度，最短弧插值）。 */
  turret: number;
  vx: number;
  vz: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  shield: boolean;
}

/** 每个玩家保留的快照时间窗（ms），约 1s。 */
const BUFFER_WINDOW_MS = 1000;
/** 快照耗尽时位置外推上限（ms），角度不外推。 */
const MAX_EXTRAPOLATE_MS = 100;

/** 把 angle 折到 (-PI, PI] 区间，供最短弧插值使用。 */
const wrapAngle = (angle: number): number => {
  let a = angle % (Math.PI * 2);
  if (a <= -Math.PI) a += Math.PI * 2;
  else if (a > Math.PI) a -= Math.PI * 2;
  return a;
};

/** 最短弧角度 lerp：从 a 到 b 沿不超过 PI 的方向插值。 */
const lerpAngle = (a: number, b: number, alpha: number): number =>
  a + wrapAngle(b - a) * alpha;

const lerp = (a: number, b: number, alpha: number): number => a + (b - a) * alpha;

/**
 * 远程坦克快照插值缓冲。
 *
 * 每个远程玩家的快照按 playerId 分队列缓存（约 1s 窗口）。渲染侧以
 * 「本地渲染时钟 − 缓冲延迟（默认 130ms）」作为采样时刻，在两帧快照间
 * 做线性插值（角度走最短弧）；采样时刻越过最新快照时，用 vx/vz 外推
 * 位置（上限 100ms，角度不外推），防止网络抖动时坦克急停。
 *
 * 时钟约定：snapshot.t 与 renderClockMs 必须处于同一时间域
 * （集成方约定为「从 match_start 起算的比赛时钟」，见 ONLINE_PVP_DESIGN §7）；
 * snapshot.vx/vz 视为「世界单位/秒」，外推按秒换算。
 */
export class RemoteBuffer {
  private readonly queues = new Map<string, TankSnapshot[]>();
  /** 渲染延迟（ms）：采样时刻 = renderClockMs − bufferDelayMs。 */
  private readonly bufferDelayMs: number;

  /**
   * @param bufferDelayMs 插值缓冲延迟（默认 110ms；约覆盖 2.2 个 20Hz 快照间隔，
   *        与 protocol.SNAPSHOT_HZ 联动调校——降频需同步加大该值）。
   */
  constructor(bufferDelayMs = 110) {
    this.bufferDelayMs = bufferDelayMs;
  }

  /** 写入一帧快照；按 t 保持队列有序，并裁剪到约 1s 窗口。 */
  upsert(playerId: string, snapshot: TankSnapshot): void {
    let queue = this.queues.get(playerId);
    if (!queue) {
      queue = [];
      this.queues.set(playerId, queue);
    }
    // 快照基本按序到达，从尾部线性找插入位即可。
    let i = queue.length;
    while (i > 0 && queue[i - 1].t > snapshot.t) i--;
    // 同 t 去重：后到的覆盖先到的。
    if (i > 0 && queue[i - 1].t === snapshot.t) queue[i - 1] = snapshot;
    else queue.splice(i, 0, snapshot);
    // 裁剪：只保留最近约 1s 的快照。
    const cutoff = queue[queue.length - 1].t - BUFFER_WINDOW_MS;
    let drop = 0;
    while (drop < queue.length - 1 && queue[drop].t < cutoff) drop++;
    if (drop > 0) queue.splice(0, drop);
  }

  /** 移除某玩家的缓冲（离开房间/对局结束时调用）。 */
  remove(playerId: string): void {
    this.queues.delete(playerId);
  }

  /** 清空全部缓冲（重开一局时调用）。 */
  clear(): void {
    this.queues.clear();
  }

  /**
   * 采样某玩家在渲染时刻的插值状态。
   *
   * @param renderClockMs 与 snapshot.t 同域的本地渲染时钟（ms）。
   * @returns 插值/外推结果；该玩家无任何快照时返回 null。
   */
  sample(playerId: string, renderClockMs: number): RemoteSample | null {
    const queue = this.queues.get(playerId);
    if (!queue || queue.length === 0) return null;

    const target = renderClockMs - this.bufferDelayMs;
    const first = queue[0];
    const last = queue[queue.length - 1];

    // 采样时刻早于最旧快照：钳到最旧快照（对局刚开场常见）。
    if (target <= first.t) return this.toSample(first, first, 0);

    // 采样时刻越过最新快照：位置按速度外推（封顶 100ms），角度不外推。
    if (target >= last.t) {
      const dt = Math.min(target - last.t, MAX_EXTRAPOLATE_MS) / 1000;
      return this.toSample(last, last, dt);
    }

    // 正常路径：在相邻两帧快照间线性插值。
    for (let i = 0; i < queue.length - 1; i++) {
      const a = queue[i];
      const b = queue[i + 1];
      if (a.t <= target && target <= b.t) {
        const span = b.t - a.t;
        const alpha = span > 0 ? (target - a.t) / span : 0;
        return {
          x: lerp(a.x, b.x, alpha),
          z: lerp(a.z, b.z, alpha),
          body: lerpAngle(a.body, b.body, alpha),
          turret: lerpAngle(a.turret, b.turret, alpha),
          vx: lerp(a.vx, b.vx, alpha),
          vz: lerp(a.vz, b.vz, alpha),
          // 离散状态取较新快照，避免血条/死亡态来回闪烁。
          hp: b.hp,
          maxHp: b.maxHp,
          alive: b.alive,
          shield: b.shield,
        };
      }
    }
    // 理论不可达（上面两个分支已覆盖边界），兜底返回最新帧。
    return this.toSample(last, last, 0);
  }

  /**
   * 边界分支统一构造结果：以 newer 帧的离散值为准，
   * dt>0 时按 base 帧速度外推位置（角度保持 base 帧不外推）。
   */
  private toSample(
    base: TankSnapshot,
    newer: TankSnapshot,
    dt: number,
  ): RemoteSample {
    return {
      x: base.x + base.vx * dt,
      z: base.z + base.vz * dt,
      body: base.body,
      turret: base.turret,
      vx: base.vx,
      vz: base.vz,
      hp: newer.hp,
      maxHp: newer.maxHp,
      alive: newer.alive,
      shield: newer.shield,
    };
  }
}
